import { SetStateAction, useContext, useState } from 'react'
import { ActionButton, FontIcon, IconButton, Stack, TextField } from '@fluentui/react'
import styles from './QuestionInput.module.css'
import { ChatMessage } from '../../api'
import { AppStateContext } from '../../state/AppProvider'
import { useChatContext } from '../../state/ChatProvider'
import { resizeImage } from '../../utils/resizeImage'
import { ArrowUp, ArrowUpDisabled, QuickPromptsIcon } from '../../assets'
import QuickPrompts from '../QuickPrompts/QuickPrompts'

interface Props {
  onSend: (question: ChatMessage['content'], id?: string) => void
  disabled: boolean
  placeholder?: string
  clearOnSend?: boolean
  conversationId?: string
}

export const QuestionInput = ({ onSend, disabled, placeholder, clearOnSend, conversationId }: Props) => {
  const [question, setQuestion] = useState<string>('')
  const [base64Image, setBase64Image] = useState<string | null>(null)

  const { isQuickPromptsOpen, setQuickPromptsOpen } = useChatContext()
  const togglePopup = () => setQuickPromptsOpen(!isQuickPromptsOpen);

  const appStateContext = useContext(AppStateContext)
  const OYD_ENABLED = appStateContext?.state.frontendSettings?.oyd_enabled || false

  const handleCardClick = (text: any) => {
    setQuestion(text || '')
    setQuickPromptsOpen(false);
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    if (file) {
      await convertToBase64(file)
    }
  }

  const convertToBase64 = async (file: Blob) => {
    try {
      const resizedBase64 = await resizeImage(file, 800, 800)
      setBase64Image(resizedBase64)
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const sendQuestion = () => {
    if (disabled || !question.trim()) {
      return
    }

    const questionTest: ChatMessage['content'] = base64Image
      ? [
        { type: 'text', text: question },
        { type: 'image_url', image_url: { url: base64Image } }
      ]
      : question.toString()

    if (conversationId && questionTest !== undefined) {
      onSend(questionTest, conversationId)
      setBase64Image(null)
    } else {
      onSend(questionTest)
      setBase64Image(null)
    }

    if (clearOnSend) {
      setQuestion('')
    }
  }

  const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
    if (ev.key === 'Enter' && !ev.shiftKey && !(ev.nativeEvent?.isComposing === true)) {
      ev.preventDefault()
      sendQuestion()
    }
  }

  const onQuestionChange = (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    setQuestion(newValue || '')
  }

  const sendQuestionDisabled = disabled || !question.trim()

  return (
    <Stack verticalFill className={styles.questionInputContainerOuter} tokens={{ childrenGap: 8 }}>
      <Stack horizontalAlign='end' tokens={{ childrenGap: 4 }}>
        <ActionButton onClick={togglePopup} text="Quick prompts" className={styles.quickPromptsButtonContainer}>
          <img src={QuickPromptsIcon} alt="" />
        </ActionButton>
      </Stack>
      <QuickPrompts
        isOpen={isQuickPromptsOpen}
        onClose={() => setQuickPromptsOpen(false)}
        onSelect={(text: SetStateAction<string>) => handleCardClick(text)}
      />
      <Stack horizontal className={styles.questionInputContainer}>
        <TextField
          className={styles.questionInputTextArea}
          styles={{ field: { paddingRight: '20px' } }}
          placeholder={placeholder}
          multiline
          resizable={false}
          borderless
          maxLength={300}
          value={question}
          onChange={onQuestionChange}
          onKeyDown={onEnterPress}
        />
        {!OYD_ENABLED && (
          <div className={styles.fileInputContainer}>
            <input
              type="file"
              id="fileInput"
              onChange={event => handleImageUpload(event)}
              accept="image/*"
              className={styles.fileInput}
            />
            <label htmlFor="fileInput" className={styles.fileLabel} aria-label="Upload Image">
              <FontIcon className={styles.fileIcon} iconName={'PhotoCollection'} aria-label="Upload Image" />
            </label>
          </div>
        )}
        {base64Image && <img className={styles.uploadedImage} src={base64Image} alt="Uploaded Preview" />}
        <div
          className={styles.questionInputSendButtonContainer}
          role="button"
          tabIndex={0}
          aria-label="Ask question button"
          onClick={sendQuestion}
          onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? sendQuestion() : null)}>
          <IconButton
            styles={{
              root: { background: '#004c97', borderRadius: '50%', padding: '1px 6px', width: '28px', height: '28px' },
              rootHovered: { background: '#1a5dab' },
              rootPressed: { background: '#003b73' },
              rootDisabled: { background: '#ececec', cursor: 'not-allowed' },
            }}
            disabled={sendQuestionDisabled}
          >
            <img src={sendQuestionDisabled ? ArrowUpDisabled : ArrowUp} />
          </IconButton>
        </div>
      </Stack>
    </Stack>
  )
}
